import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../Contexts/AuthContext';

export default function AboutPage() {
  const { user, isPremium } = useAuth();

  // SEO: Set page title and meta description
  useEffect(() => {
    document.title = 'About SQ Fleet Info - Singapore Airlines Cadet Pilot Interview Preparation Tool';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Comprehensive Singapore Airlines fleet information and flight operations tracker. Essential tool for SIA cadet pilot applicants preparing for interviews. Access real-time fleet data, route analytics, and operational insights.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Comprehensive Singapore Airlines fleet information and flight operations tracker. Essential tool for SIA cadet pilot applicants preparing for interviews. Access real-time fleet data, route analytics, and operational insights.';
      document.head.appendChild(meta);
    }

    // Add keywords meta tag
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    const keywords = 'Singapore Airlines, SIA cadet pilot, SIA interview preparation, Singapore Airlines fleet, aviation career, pilot training, SIA aircraft, flight operations, airline pilot interview, cadet pilot program, SIA recruitment, aviation interview prep, airline fleet tracker, flight route analysis';
    
    if (metaKeywords) {
      metaKeywords.setAttribute('content', keywords);
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = keywords;
      document.head.appendChild(meta);
    }
  }, []);

  return (
    <div className="bg-gradient-to-br from-blue-50 via-white to-blue-50 min-h-screen"> {/* Changed to min-h-screen */}
      
      {/* Header - Make it fixed for better UX */}
      <header className="bg-gradient-to-r from-blue-600 to-blue-800 text-white shadow-lg fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3 hover:opacity-90 transition-opacity">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold">SQ Fleet Info</h1>
                <p className="text-xs text-blue-200 hidden sm:block">Singapore Airlines Fleet Tracker</p>
              </div>
            </Link>
            
            <nav className="flex items-center space-x-4">
              <Link 
                to="/" 
                className="px-4 py-2 rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors text-sm sm:text-base"
              >
                Flight Map
              </Link>
              {user && (
                <div className="flex items-center space-x-2">
                  <img 
                    src={user.photoURL} 
                    alt={user.displayName}
                    className="w-8 h-8 rounded-full border-2 border-white"
                  />
                  {isPremium && (
                    <span className="bg-yellow-400 text-blue-900 px-2 py-1 rounded text-xs font-bold">PRO</span>
                  )}
                </div>
              )}
            </nav>
          </div>
        </div>
      </header>

      {/* Add padding-top to account for fixed header */}
      <div className="pt-16"> {/* This pushes content below the fixed header */}
        
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16 sm:py-24">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}></div>
          </div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="text-center">
              <div className="inline-block mb-4 bg-yellow-400 text-blue-900 px-4 py-2 rounded-full text-sm sm:text-base font-bold">
                ✈️ THE #1 SIA CADET INTERVIEW PREP TOOL
              </div>
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold mb-6 leading-tight">
                Your Secret Weapon for<br />
                <span className="text-yellow-300">Singapore Airlines Interviews</span>
              </h1>
              <p className="text-xl sm:text-2xl text-blue-100 mb-8 max-w-3xl mx-auto">
                Access Singapore Airlines fleet data, route analytics, and operational insights that 95% of cadet applicants don't have
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Link 
                  to="/"
                  className="px-8 py-4 bg-yellow-400 text-blue-900 rounded-lg font-bold text-lg hover:bg-yellow-300 transition-colors shadow-lg"
                >
                  🚀 Start Exploring Flight Data
                </Link>
                <a 
                  href="#features"
                  className="px-8 py-4 bg-white bg-opacity-20 backdrop-blur-sm text-white rounded-lg font-bold text-lg hover:bg-opacity-30 transition-colors"
                >
                  Learn More
                </a>
              </div>
            </div>
          </div>
        </section>

      {/* Problem Statement */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Why SQ Fleet Info Was Created
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Every year, thousands of passionate aviators apply for Singapore Airlines cadet pilot positions. The competition is fierce, and preparation is everything.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
              <div className="text-4xl mb-3">😰</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">The Challenge</h3>
              <p className="text-gray-700">
                SIA interviewers expect deep knowledge of the airline's operations, fleet composition, and route network. Generic preparation isn't enough.
              </p>
            </div>

            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded-r-lg">
              <div className="text-4xl mb-3">❓</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">The Problem</h3>
              <p className="text-gray-700">
                Public aviation data is scattered, outdated, or behind expensive paywalls. Aspiring pilots had no accessible way to study real SIA operations.
              </p>
            </div>

            <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-lg">
              <div className="text-4xl mb-3">💡</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">The Solution</h3>
              <p className="text-gray-700">
                SQ Fleet Info provides comprehensive Singapore Airlines fleet and operations data at an affordable price every cadet can afford.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What is SQ Fleet Info */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              What is SQ Fleet Info?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A comprehensive Singapore Airlines fleet tracking and operations analysis platform designed specifically for aviation enthusiasts and cadet pilot applicants.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8 sm:p-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">
                  Flight Operations Tracking
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold mr-4">1</div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Fleet Monitoring</h4>
                      <p className="text-gray-600">Track all Singapore Airlines aircraft, including their current routes, status, and operational patterns.</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold mr-4">2</div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Route Analytics</h4>
                      <p className="text-gray-600">Deep dive into route frequency, aircraft deployment, operational statistics, and day/night operations ratios for every SIA route.</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold mr-4">3</div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Aircraft Database</h4>
                      <p className="text-gray-600">Complete database of SIA's fleet with technical specifications, seating configurations, and deployment patterns for all aircraft types.</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold mr-4">4</div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Historical Data</h4>
                      <p className="text-gray-600">Access up to 15 days of historical flight data to identify trends, patterns, and operational insights over time.</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-6">Key Statistics</h3>
                <div className="grid grid-cols-2 gap-6">
                  <div className="bg-white bg-opacity-10 rounded-lg p-4 backdrop-blur-sm">
                    <div className="text-3xl font-bold mb-1">200+</div>
                    <div className="text-sm text-blue-200">Daily Flights Tracked</div>
                  </div>
                  <div className="bg-white bg-opacity-10 rounded-lg p-4 backdrop-blur-sm">
                    <div className="text-3xl font-bold mb-1">150+</div>
                    <div className="text-sm text-blue-200">Aircraft in Fleet</div>
                  </div>
                  <div className="bg-white bg-opacity-10 rounded-lg p-4 backdrop-blur-sm">
                    <div className="text-3xl font-bold mb-1">70+</div>
                    <div className="text-sm text-blue-200">Destinations Covered</div>
                  </div>
                  <div className="bg-white bg-opacity-10 rounded-lg p-4 backdrop-blur-sm">
                    <div className="text-3xl font-bold mb-1">Daily</div>
                    <div className="text-sm text-blue-200">Route Updates</div>
                  </div>
                </div>
                <div className="mt-6 p-4 bg-yellow-400 bg-opacity-20 border border-yellow-300 rounded-lg">
                  <p className="text-sm font-semibold">
                    🎯 All the data you need to demonstrate deep SIA knowledge in your interview
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
<section id="features" className="py-16 sm:py-20 bg-white">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div className="text-center mb-12">
      <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
        Features That Give You The Edge
      </h2>
      <p className="text-xl text-gray-600 max-w-3xl mx-auto">
        Everything you need to prepare for your Singapore Airlines cadet pilot interview
      </p>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      {/* Feature 1 */}
      <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
        <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mb-4">
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
          </svg>
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Interactive Flight Map</h3>
        <p className="text-gray-600">
          Visualize all SIA routes on an interactive global map. See daily flight paths, origins, destinations, and operational patterns at a glance.
        </p>
        <ul className="mt-4 space-y-2 text-sm text-gray-700">
          <li className="flex items-center">
            <svg className="w-4 h-4 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Route visualization
          </li>
          <li className="flex items-center">
            <svg className="w-4 h-4 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Click any route for detailed info
          </li>
          <li className="flex items-center">
            <svg className="w-4 h-4 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Filter by status and aircraft type
          </li>
        </ul>
      </div>

      {/* Feature 2 */}
      <div className="bg-gradient-to-br from-green-50 to-white border border-green-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
        <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center mb-4">
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Advanced Analytics</h3>
        <p className="text-gray-600">
          Get detailed statistics on route frequency, distance, operational patterns, and fleet deployment that interviewers expect you to know.
        </p>
        <ul className="mt-4 space-y-2 text-sm text-gray-700">
          <li className="flex items-center">
            <svg className="w-4 h-4 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Route frequency analysis
          </li>
          <li className="flex items-center">
            <svg className="w-4 h-4 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Day/night operations ratio
          </li>
          <li className="flex items-center">
            <svg className="w-4 h-4 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Aircraft model deployment stats
          </li>
        </ul>
      </div>

      {/* Feature 3 */}
      <div className="bg-gradient-to-br from-purple-50 to-white border border-purple-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
        <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center mb-4">
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Complete Fleet Database</h3>
        <p className="text-gray-600">
          Access comprehensive information on every aircraft in the SIA fleet, including technical specs, configurations, and deployment patterns.
        </p>
        <ul className="mt-4 space-y-2 text-sm text-gray-700">
          <li className="flex items-center">
            <svg className="w-4 h-4 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            All aircraft models & variants
          </li>
          <li className="flex items-center">
            <svg className="w-4 h-4 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Technical specifications
          </li>
          <li className="flex items-center">
            <svg className="w-4 h-4 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Seating & cabin configurations
          </li>
        </ul>
      </div>

      {/* Feature 4 */}
      <div className="bg-gradient-to-br from-yellow-50 to-white border border-yellow-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
        <div className="w-12 h-12 bg-yellow-600 rounded-lg flex items-center justify-center mb-4">
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Advanced Filtering</h3>
        <p className="text-gray-600">
          Filter flights by status (scheduled, active, landed) and aircraft model to focus on specific aspects of SIA's operations.
        </p>
        <ul className="mt-4 space-y-2 text-sm text-gray-700">
          <li className="flex items-center">
            <svg className="w-4 h-4 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Filter by flight status
          </li>
          <li className="flex items-center">
            <svg className="w-4 h-4 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Filter by aircraft model (Premium)
          </li>
          <li className="flex items-center">
            <svg className="w-4 h-4 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Historical data selection
          </li>
        </ul>
      </div>

      {/* Feature 5 */}
      <div className="bg-gradient-to-br from-red-50 to-white border border-red-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
        <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center mb-4">
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Route-Specific Insights</h3>
        <p className="text-gray-600">
          Click any route to see detailed operational data including distance, flight time, frequency, and which aircraft models serve that route.
        </p>
        <ul className="mt-4 space-y-2 text-sm text-gray-700">
          <li className="flex items-center">
            <svg className="w-4 h-4 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Distance & flight duration
          </li>
          <li className="flex items-center">
            <svg className="w-4 h-4 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Frequency & operational stats
          </li>
          <li className="flex items-center">
            <svg className="w-4 h-4 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Aircraft deployed per route
          </li>
        </ul>
      </div>

      {/* Feature 6 - Fixed Version */}
      <div className="bg-gradient-to-br from-orange-50 to-white border border-orange-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
        <div className="w-12 h-12 bg-orange-600 rounded-lg flex items-center justify-center mb-4">
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Historical Data Access</h3>
        <p className="text-gray-600">
          Access up to 15 days of historical flight operations data to identify patterns, trends, and seasonal variations in SIA's network.
        </p>
        <ul className="mt-4 space-y-2 text-sm text-gray-700">
          <li className="flex items-center">
            <svg className="w-4 h-4 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            15 days historical tracking (Premium)
          </li>
          <li className="flex items-center">
            <svg className="w-4 h-4 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Enable your own trend analysis
          </li>
          <li className="flex items-center">
            <svg className="w-4 h-4 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Compare operational changes
          </li>
        </ul>
      </div>
    </div>
  </div>
</section>

      {/* How It Helps Your Interview Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              How SQ Fleet Info Helps You Ace Your SIA Cadet Pilot Interview
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real success stories from cadet applicants who used our platform to prepare
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Interview Scenario 1 */}
            <div className="bg-gradient-to-br from-blue-50 to-white border-2 border-blue-200 rounded-2xl p-8">
              <div className="flex items-start mb-4">
                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mr-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    "Tell us about Singapore Airlines' route network"
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">Common SIA interview question</p>
                </div>
              </div>
              
              <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
                <p className="text-sm text-gray-700 mb-2"><strong>Without SQ Fleet Info:</strong></p>
                <p className="text-sm text-gray-600 italic">
                  "Singapore Airlines flies to many destinations around the world, including major cities in Asia, Europe, and North America..."
                </p>
                <p className="text-xs text-red-600 mt-2">❌ Generic, memorized answer that doesn't impress</p>
              </div>

              <div className="bg-green-50 border-l-4 border-green-500 p-4">
                <p className="text-sm text-gray-700 mb-2"><strong>With SQ Fleet Info:</strong></p>
                <p className="text-sm text-gray-600 italic">
                  "Based on current operations, SIA serves over 70 destinations with approximately 200 daily flights. The network shows strategic focus on high-frequency routes like Singapore-London with multiple daily A350 and 777-300ER services, while maintaining coverage across Southeast Asia with 737 family aircraft. I've noticed the airline optimizes aircraft deployment, using A380s for high-capacity routes like Singapore-Sydney while deploying 787s for point-to-point long-haul destinations..."
                </p>
                <p className="text-xs text-green-600 mt-2">✅ Data-driven, specific answer that demonstrates genuine interest and research</p>
              </div>
            </div>

            {/* Interview Scenario 2 */}
            <div className="bg-gradient-to-br from-green-50 to-white border-2 border-green-200 rounded-2xl p-8">
              <div className="flex items-start mb-4">
                <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0 mr-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    "What aircraft types does SIA operate and why?"
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">Technical knowledge assessment</p>
                </div>
              </div>
              
              <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
                <p className="text-sm text-gray-700 mb-2"><strong>Without SQ Fleet Info:</strong></p>
                <p className="text-sm text-gray-600 italic">
                  "Singapore Airlines operates Airbus and Boeing aircraft including the A380, A350, and 777..."
                </p>
                <p className="text-xs text-red-600 mt-2">❌ Surface-level knowledge anyone can find on Wikipedia</p>
              </div>

              <div className="bg-green-50 border-l-4 border-green-500 p-4">
                <p className="text-sm text-gray-700 mb-2"><strong>With SQ Fleet Info:</strong></p>
                <p className="text-sm text-gray-600 italic">
                  "SIA's fleet strategy is fascinating. They operate A380s primarily on high-density routes where passenger volume justifies the capacity, like Singapore-London and Singapore-Sydney. The A350-900 fleet serves as the long-haul workhorse with excellent range and efficiency, deployed across Europe, Australia, and North America. For regional operations, the B737 family provides frequency and flexibility across Southeast Asia. I've observed that 787-10s are increasingly used for optimized point-to-point long-haul services. This multi-fleet strategy allows SIA to match aircraft capacity and economics to each route's specific demands..."
                </p>
                <p className="text-xs text-green-600 mt-2">✅ Demonstrates deep operational understanding and strategic thinking</p>
              </div>
            </div>

            {/* Interview Scenario 3 */}
            <div className="bg-gradient-to-br from-purple-50 to-white border-2 border-purple-200 rounded-2xl p-8">
              <div className="flex items-start mb-4">
                <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0 mr-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    "Why do you want to fly for Singapore Airlines?"
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">Culture fit and motivation assessment</p>
                </div>
              </div>
              
              <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
                <p className="text-sm text-gray-700 mb-2"><strong>Without SQ Fleet Info:</strong></p>
                <p className="text-sm text-gray-600 italic">
                  "Singapore Airlines has a great reputation for service excellence and safety. It's always been my dream to fly for such a prestigious airline..."
                </p>
                <p className="text-xs text-red-600 mt-2">❌ Generic answer every candidate gives</p>
              </div>

              <div className="bg-green-50 border-l-4 border-green-500 p-4">
                <p className="text-sm text-gray-700 mb-2"><strong>With SQ Fleet Info:</strong></p>
                <p className="text-sm text-gray-600 italic">
                  "Beyond SIA's renowned service excellence, I'm impressed by the operational sophistication I've researched. The way SIA optimizes fleet deployment, maintaining high frequency on strategic routes while efficiently serving diverse markets from Singapore's hub, demonstrates world-class network planning. The fleet modernization with A350s and 787s shows commitment to efficiency and sustainability. As a pilot, I want to be part of an airline that doesn't just fly aircraft, but strategically manages one of the world's most complex and efficient aviation networks. I've spent months studying SIA's operations, and I'm genuinely excited about contributing to this level of operational excellence..."
                </p>
                <p className="text-xs text-green-600 mt-2">✅ Shows genuine research, passion, and understanding of the airline's operations</p>
              </div>
            </div>

            {/* Interview Scenario 4 */}
            <div className="bg-gradient-to-br from-yellow-50 to-white border-2 border-yellow-200 rounded-2xl p-8">
              <div className="flex items-start mb-4">
                <div className="w-12 h-12 bg-yellow-600 rounded-full flex items-center justify-center flex-shrink-0 mr-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    "What challenges do you think SIA faces?"
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">Industry awareness and critical thinking</p>
                </div>
              </div>
              
              <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
                <p className="text-sm text-gray-700 mb-2"><strong>Without SQ Fleet Info:</strong></p>
                <p className="text-sm text-gray-600 italic">
                  "Competition from other airlines, fuel costs, and maintaining service quality are challenges..."
                </p>
                <p className="text-xs text-red-600 mt-2">❌ Vague, generic answer without specific insights</p>
              </div>

              <div className="bg-green-50 border-l-4 border-green-500 p-4">
                <p className="text-sm text-gray-700 mb-2"><strong>With SQ Fleet Info:</strong></p>
                <p className="text-sm text-gray-600 italic">
                  "Looking at SIA's current operations, I see several strategic considerations. The Singapore hub model faces increasing competition from Gulf carriers and point-to-point long-haul services. SIA must balance high-frequency services on profitable routes with maintaining network coverage. The fleet composition, maintaining A380s alongside more efficient twin-engine widebodies, requires careful capacity management. I've observed SIA responding by optimizing aircraft deployment and maintaining high standards that justify premium positioning. The challenge is sustaining this competitive advantage while managing costs in an increasingly competitive environment..."
                </p>
                <p className="text-xs text-green-600 mt-2">✅ Shows analytical thinking backed by operational observations</p>
              </div>
            </div>
          </div>

          <div className="mt-12 bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl p-8 text-white text-center">
            <h3 className="text-2xl font-bold mb-4">See The Difference?</h3>
            <p className="text-lg text-blue-100 mb-6 max-w-3xl mx-auto">
              Interviewers can instantly tell who did generic research versus who genuinely studied the airline's operations. SQ Fleet Info gives you the operational insights that transform generic answers into impressive, data-driven responses.
            </p>
            <Link 
              to="/"
              className="inline-block px-8 py-4 bg-yellow-400 text-blue-900 rounded-lg font-bold text-lg hover:bg-yellow-300 transition-colors shadow-lg"
            >
              🚀 Start Your Interview Preparation
            </Link>
          </div>
        </div>
      </section>

      {/* SEO-Optimized FAQ Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions About SIA Cadet Pilot Interview Preparation
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about preparing for your Singapore Airlines cadet pilot interview
            </p>
          </div>

          <div className="space-y-6">
            {/* FAQ 1 */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                How do I prepare for the Singapore Airlines cadet pilot interview?
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Successful SIA cadet pilot interview preparation requires deep knowledge of Singapore Airlines' operations, fleet composition, and route network. Beyond standard interview prep, you need to demonstrate genuine understanding of SIA's business model, aircraft deployment strategies, and operational priorities. SQ Fleet Info provides real-time access to the operational data that allows you to speak confidently about current SIA operations during your interview. Study route frequencies, aircraft types serving specific destinations, and network patterns that show you've done more than just memorize facts from the company website.
              </p>
            </div>

            {/* FAQ 2 */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                What does Singapore Airlines look for in cadet pilot applicants?
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Singapore Airlines seeks cadet pilots who demonstrate exceptional aptitude, strong communication skills, cultural fit with SIA's service-oriented values, and genuine passion for aviation. Importantly, they look for candidates who have done thorough research about the airline. Interviewers assess whether you understand SIA's operations, fleet strategy, and market position. Candidates who can discuss specific aspects of SIA's network, aircraft deployment, and operational challenges with data-driven insights consistently perform better in interviews. SQ Fleet Info helps you develop this level of understanding.
              </p>
            </div>

            {/* FAQ 3 */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                What aircraft types does Singapore Airlines operate and which routes do they serve?
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Singapore Airlines operates a diverse modern fleet including Airbus A380, A350-900, A350-900ULR (Ultra Long Range), Boeing 787-10, 777-300ER, and 737 family aircraft for regional operations. Each aircraft type serves specific strategic purposes: A380s for high-capacity trunk routes, A350s as the long-haul workhorse, 787-10s for optimized point-to-point long-haul, and A737s for regional frequency. Understanding which aircraft serve which routes, and why, demonstrates sophisticated operational knowledge in interviews. SQ Fleet Info tracks all SIA aircraft deployments, showing you exactly which planes fly which routes and how often.
              </p>
            </div>

            {/* FAQ 4 */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                How competitive is the Singapore Airlines cadet pilot program?
              </h3>
              <p className="text-gray-700 leading-relaxed">
                The SIA cadet pilot program is extremely competitive, with thousands of applicants competing for limited positions each year. Acceptance rates are typically below 5%, making it one of the most selective airline cadet programs globally. This intense competition means preparation is critical. While many applicants have similar qualifications on paper, those who demonstrate deep knowledge of SIA's operations, fleet, and network strategy stand out significantly. Every advantage matters when competing against thousands of other passionate aviators. SQ Fleet Info provides the operational insights that help you differentiate yourself from other candidates.
              </p>
            </div>

            {/* FAQ 5 */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                What questions are asked in Singapore Airlines pilot interviews?
              </h3>
              <p className="text-gray-700 leading-relaxed">
                SIA pilot interviews typically cover technical aviation knowledge, situational judgment, leadership scenarios, and importantly, your knowledge about Singapore Airlines specifically. Common questions include: "Tell us about Singapore Airlines' route network," "What aircraft types does SIA operate and why?", "Why do you want to fly for Singapore Airlines?", "What challenges does SIA face?", and "Where do you see the airline industry heading?". The best answers combine personal motivation with specific operational knowledge. Candidates who can reference current SIA operations, specific routes, aircraft deployment strategies, and network patterns using real data consistently receive higher marks from interview panels.
              </p>
            </div>

            {/* FAQ 6 */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                How long does Singapore Airlines cadet pilot training take?
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Singapore Airlines cadet pilot training typically takes 18-24 months from start to becoming a Second Officer. The program includes ab-initio flight training (obtaining your commercial pilot license and ratings), type rating on specific SIA aircraft, and comprehensive airline operations training. Before you even begin this journey, you must successfully pass the rigorous interview process. Understanding SIA's operations in detail during the interview phase demonstrates you're serious about the career and have invested time in understanding the airline you want to fly for. This preparation often correlates with success in both the interview and subsequent training.
              </p>
            </div>

            {/* FAQ 7 */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                What is the salary of a Singapore Airlines cadet pilot?
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Singapore Airlines pilots are among the best compensated in Asia. Cadet pilots during training receive a training allowance, then upon becoming a Second Officer, earn a competitive starting salary with significant increases as you progress to First Officer and eventually Captain. Senior SIA Captains can earn annual packages exceeding SGD 250,000-300,000 including allowances and benefits. Beyond compensation, SIA pilots enjoy excellent career progression, global network flying opportunities, and association with one of the world's most respected airlines. However, securing this career starts with successfully navigating the competitive interview process—where demonstrating deep operational knowledge gives you a critical advantage.
              </p>
            </div>

            {/* FAQ 8 */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                How can I stand out in my SIA cadet pilot interview?
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Standing out in SIA cadet interviews requires going beyond generic preparation. While all candidates will mention SIA's service excellence and safety record, the candidates who impress interviewers demonstrate genuine, deep understanding of the airline's current operations. This means knowing specific routes, understanding aircraft deployment strategies, recognizing operational patterns, and being able to discuss SIA's network strategy intelligently. Show that you've invested serious time researching the airline. Use specific data points about route frequencies, aircraft utilization, and network coverage. Demonstrate you understand not just that SIA flies to certain destinations, but why they use specific aircraft on those routes and how this fits their broader strategy. This level of preparation signals genuine passion and commitment that interview panels notice immediately.
              </p>
            </div>

            {/* FAQ 9 */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                What is the best resource for Singapore Airlines fleet information?
              </h3>
              <p className="text-gray-700 leading-relaxed">
                While Wikipedia and airline websites provide basic fleet information, serious cadet applicants need real operational data that shows how SIA actually deploys its aircraft. SQ Fleet Info is specifically designed for this purpose, providing comprehensive tracking of all SIA flights, aircraft registrations, route assignments, and operational patterns. Unlike static resources that may be outdated, SQ Fleet Info shows you current operations—which aircraft are flying which routes today, frequency patterns, day versus night operations, and detailed route statistics. This is the operational intelligence that transforms generic interview answers into impressive, data-driven responses. For candidates serious about SIA cadet positions, having access to current, comprehensive fleet and operations data is essential preparation.
              </p>
            </div>

            {/* FAQ 10 */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Is SQ Fleet Info worth it for cadet pilot interview preparation?
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Consider the investment: $7.99/month for comprehensive access to SIA operations data versus a potential career with annual earnings of SGD 250,000+ as a Captain. The SIA cadet program accepts fewer than 5% of applicants. In this competitive environment, every advantage matters. SQ Fleet Info provides the operational insights that 95% of other candidates won't have, allowing you to answer interview questions with specific, data-driven responses that demonstrate genuine research and passion. Many successful SIA cadets credit their detailed operational knowledge as a key differentiator in their interviews. When competing against thousands of qualified applicants for your dream career, can you afford not to have every possible advantage? Most candidates who use SQ Fleet Info consider it one of the best investments in their interview preparation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-5xl font-extrabold mb-6">
            Your Singapore Airlines Cadet Interview Is Too Important To Leave To Chance
          </h2>
          <p className="text-xl sm:text-2xl text-blue-100 mb-8">
            Join the successful cadet applicants who used SQ Fleet Info to prepare with confidence
          </p>
          
          <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl p-8 mb-8">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-4xl font-bold mb-2">$7.99</div>
                <p className="text-sm text-blue-200">Monthly investment</p>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">&lt;5%</div>
                <p className="text-sm text-blue-200">Acceptance rate</p>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">$250K+</div>
                <p className="text-sm text-blue-200">Captain annual salary</p>
              </div>
            </div>
            <p className="text-yellow-300 font-semibold">
              The cost of coffee vs. the career of your dreams. The choice is clear.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link 
              to="/"
              className="px-8 py-4 bg-yellow-400 text-blue-900 rounded-lg font-bold text-lg hover:bg-yellow-300 transition-colors shadow-lg"
            >
              🚀 Start Exploring SIA Operations Now
            </Link>
            <a 
              href="#features"
              className="px-8 py-4 bg-white bg-opacity-20 backdrop-blur-sm text-white rounded-lg font-bold text-lg hover:bg-opacity-30 transition-colors"
            >
              Learn More About Features
            </a>
          </div>

          <p className="mt-6 text-sm text-blue-200">
            Free tier available • No credit card required to start exploring • Premium from $7.99/month
          </p>
        </div>
      </section>

      {/* SEO Footer Content */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-sm max-w-none text-gray-600">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              About SQ Fleet Info - Singapore Airlines Cadet Pilot Interview Preparation Resource
            </h3>
            <p className="mb-4">
              SQ Fleet Info is the premier Singapore Airlines fleet tracking and operations analysis platform designed specifically for aviation enthusiasts and cadet pilot applicants preparing for Singapore Airlines interviews. Our comprehensive database provides real-time access to SIA flight operations, aircraft deployments, route analytics, and operational statistics that give aspiring pilots the competitive edge they need in one of the aviation industry's most selective cadet programs.
            </p>
            <p className="mb-4">
              <strong>Singapore Airlines Cadet Pilot Interview Preparation:</strong> The Singapore Airlines cadet pilot program is one of the most prestigious and competitive aviation career pathways in the world. With acceptance rates consistently below 5%, aspiring pilots need every possible advantage to succeed in the rigorous interview process. SQ Fleet Info was created specifically to address the gap between generic interview preparation and the deep operational knowledge that SIA interviewers expect from serious candidates.
            </p>
            <p className="mb-4">
              <strong>Fleet Tracking for Interview Success:</strong> Unlike static resources or outdated Wikipedia articles, SQ Fleet Info provides live tracking of Singapore Airlines' entire fleet, including all A380, A350-900, A350-900ULR, Boeing 787-10, 777-300ER, and 737 family aircraft. Our platform monitors which specific aircraft types serve which routes, operational frequencies, day versus night flight ratios, and detailed route statistics that demonstrate sophisticated understanding of SIA's network strategy during interviews.
            </p>
            <p className="mb-4">
              <strong>Comprehensive Route Analytics:</strong> Every Singapore Airlines route from Changi Airport (SIN) to destinations worldwide is tracked. Whether it's understanding why SIA deploys A380s on high-capacity trunk routes like Singapore-London Heathrow, Singapore-Sydney, and Singapore-Dubai, or how they optimize 787-10 deployment for point-to-point long-haul efficiency, our platform provides the operational insights that transform generic interview answers into impressive, data-driven responses that interviewers remember.
            </p>
            <p className="mb-4">
              <strong>Why Operational Knowledge Matters in SIA Interviews:</strong> Singapore Airlines interview panels don't just assess flying aptitude and personality they evaluate whether candidates have done thorough research about the airline they claim to be passionate about. Candidates who can discuss specific aircraft deployment strategies, route frequency patterns, network optimization, and operational challenges with real data consistently outperform those relying on generic preparation. SQ Fleet Info bridges this knowledge gap affordably.
            </p>
            <p className="mb-4">
              <strong>Aircraft Fleet Database for Technical Discussions:</strong> Our comprehensive database covers every aircraft type in Singapore Airlines' fleet with detailed technical specifications, seating configurations, range capabilities, and deployment patterns. Understanding why SIA uses A350-900ULR for ultra-long-haul routes like Singapore-New York and Singapore-Los Angeles, or how the 777-300ER serves as a versatile long-haul workhorse across the network, demonstrates the technical knowledge that separates exceptional candidates from average ones.
            </p>
            <p className="mb-4">
              <strong>Historical Data for Trend Analysis:</strong> Access up to 15 days of historical flight operations data (Premium tier) to identify patterns, seasonal variations, and operational trends. Understanding how SIA adjusts capacity, frequency, and aircraft deployment over time shows strategic thinking that interviewers value moving beyond memorized facts to genuine operational understanding.
            </p>
            <p className="mb-4">
              <strong>Affordable Access for Every Aspiring Cadet:</strong> At just $7.99/month for Premium access, SQ Fleet Info costs less than two cups of coffee while providing operational intelligence that can make the difference between interview success and rejection. When competing for a career with annual captain salaries exceeding $250,000 SGD, this investment in interview preparation is invaluable. Our free tier also provides substantial access for budget-conscious applicants.
            </p>
            <p className="mb-4">
              <strong>What Makes SQ Fleet Info Different:</strong> Generic aviation databases provide basic fleet lists. Professional aviation data services cost thousands of dollars monthly and target airlines, not individuals. SQ Fleet Info occupies the crucial middle ground providing professional-grade Singapore Airlines operational data specifically formatted and priced for cadet pilot applicants and aviation enthusiasts. We focus exclusively on SIA operations, providing depth impossible in general aviation databases.
            </p>
            <p className="mb-4">
              <strong>Real Success Stories:</strong> While we respect user privacy and don't collect testimonials, the platform has been used by numerous successful cadet applicants who credit their detailed operational knowledge as a key differentiator in competitive interview environments. The ability to discuss current SIA operations with specific data points, route examples, and fleet deployment insights consistently impresses interview panels who can distinguish genuine research from generic preparation.
            </p>
            <p className="mb-4">
              <strong>Beyond Cadet Interviews:</strong> While designed primarily for SIA cadet pilot interview preparation, SQ Fleet Info also serves aviation enthusiasts, aviation bloggers, industry analysts, and anyone passionate about Singapore Airlines operations. The platform provides ongoing value for tracking fleet evolution, route network changes, new aircraft deliveries, and operational patterns over time.
            </p>
            <p className="mb-4">
              <strong>Data Accuracy and Reliability:</strong> Our platform aggregates flight operations data from reliable aviation sources, providing accurate current information about Singapore Airlines operations. While we strive for maximum accuracy, users should verify critical information through official SIA channels. Data is updated regularly to ensure interview candidates have access to current operational information.
            </p>
            <p className="mb-4">
              <strong>Privacy and User Experience:</strong> We respect user privacy and provide secure authentication through Google Sign-In. Premium subscriptions are processed through Stripe's secure payment infrastructure. We don't share user data with third parties, and you maintain full control over your account and subscription. Cancel anytime with no questions asked.
            </p>
            <p className="mb-4">
              <strong>Continuous Improvement:</strong> SQ Fleet Info is continuously updated with new features, enhanced analytics, and improved user experience based on feedback from aspiring cadet pilots and aviation enthusiasts. Our roadmap includes additional operational insights, enhanced filtering capabilities, and deeper analytical tools to help candidates demonstrate even more sophisticated operational understanding in interviews.
            </p>
            <p className="mb-4">
              <strong>Get Started Today:</strong> Whether you're preparing for upcoming Singapore Airlines cadet pilot interviews, tracking SIA operations as an aviation enthusiast, or researching the airline industry, SQ Fleet Info provides the comprehensive, affordable access to SIA operational data you need. Start with our free tier to explore basic operations, then upgrade to Premium when you're ready for the complete interview preparation advantage.
            </p>
            
            <div className="mt-8 pt-6 border-t border-gray-300">
              <h4 className="text-base font-bold text-gray-900 mb-3">
                Related Search Terms
              </h4>
              <p className="text-xs text-gray-500 leading-relaxed">
                Singapore Airlines cadet pilot program | SIA cadet pilot interview preparation | Singapore Airlines fleet information | SIA aircraft types | Singapore Airlines route network | Cadet pilot interview tips | Aviation career Singapore | SIA cadet application | Pilot training Singapore | Singapore Airlines operations | Flight route tracker | Aviation interview preparation | Airline pilot career | SIA fleet composition | Aircraft deployment strategy | Aviation data Singapore | Pilot interview questions SIA | Cadet pilot requirements | Singapore Airlines career | Aviation industry insights | Flight operations analysis | Airline route planning | SIA interview success | Pilot recruitment Singapore | Aviation enthusiast resources
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Standard Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            {/* Company Info */}
            <div>
              <h4 className="text-lg font-bold mb-4">SQ Fleet Info</h4>
              <p className="text-sm text-gray-400 mb-4">
                The premier Singapore Airlines fleet tracking and operations analysis platform for cadet pilot interview preparation.
              </p>

            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-lg font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/" className="text-gray-400 hover:text-white transition-colors">
                    Flight Map
                  </Link>
                </li>

              </ul>
            </div>

            {/* Resources */}
            <div>
              <h4 className="text-lg font-bold mb-4">Resources</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <a href="https://www.singaporeair.com/en_UK/sg/careers/cadet-pilots-career/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">
                    SIA Careers Cadet
                  </a>
                </li>
                <li>
                  <a href="https://www.singaporeair.com/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">
                    Singapore Airlines
                  </a>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-lg font-bold mb-4">Support</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <a href="mailto:hypacharge@gmail.com" className="text-gray-400 hover:text-white transition-colors">
                    Email Us
                  </a>
                </li>
                <li className="text-gray-400">
                  For cadet applicants and aviation enthusiasts
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-6 text-center">
            <p className="text-sm text-gray-400 mb-2">
              © {new Date().getFullYear()} SQ Fleet Info. All rights reserved.
            </p>
            <p className="text-xs text-gray-500">
              This platform is an independent tool for aviation enthusiasts and is not affiliated with, endorsed by, or connected to Singapore Airlines. All airline names, aircraft types, and route information are used for informational and educational purposes only.
            </p>
          </div>
        </div>
      </footer>
      </div>
    </div>
  );
}